import { useId } from 'react';

import type { ClientProfile, FooterDetailsInput } from '../lib/ordersApi.js';

import { FIELD_LIMITS } from '../lib/fieldLimits.js';

import { CountrySelect } from './CountrySelect.js';
import { DISABLED_CHECKBOX, TextAreaField, TextField } from './ui.js';

/**
 * The local section — intake v2, section C. Optional, and shared by the order
 * form and the project form so the two cannot drift apart.
 *
 * One target location, not a list: a build is made around one town or area, and
 * a list of twelve of them was never used for anything.
 *
 * The footer is a set of **parameters**, not markup. Every field is length
 * capped here as well as on the server, and the map arrives as the snippet
 * Google gives you — the server extracts the address out of it and keeps only
 * that. What is stored is never what was pasted.
 */

export const MAX_TARGET_LOCATION = FIELD_LIMITS.targetLocation;

export interface FooterForm {
  businessName: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postcode: string;
  country: string;
  mapsEmbed: string;
}

export interface LocalSectionValue {
  targetLocation: string;
  footerEnabled: boolean;
  footer: FooterForm;
}

export type FooterField = keyof FooterForm;

export interface LocalSectionErrors {
  targetLocation?: string | undefined;
  footer?: Partial<Record<FooterField, string>> | undefined;
}

export function emptyFooterForm(): FooterForm {
  return {
    businessName: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    postcode: '',
    country: '',
    mapsEmbed: '',
  };
}

export function emptyLocalSection(): LocalSectionValue {
  return { targetLocation: '', footerEnabled: false, footer: emptyFooterForm() };
}

/** The local section of a project, as the form shows it. */
export function localSectionFrom(profile: ClientProfile): LocalSectionValue {
  const stored = profile.footerNap;
  if (!stored) {
    return {
      targetLocation: profile.targetLocation ?? '',
      footerEnabled: false,
      footer: emptyFooterForm(),
    };
  }

  return {
    targetLocation: profile.targetLocation ?? '',
    footerEnabled: true,
    footer: {
      businessName: stored.businessName ?? '',
      phone: stored.phone ?? '',
      addressLine1: stored.addressLine1 ?? '',
      addressLine2: stored.addressLine2 ?? '',
      city: stored.city ?? '',
      region: stored.region ?? '',
      postcode: stored.postcode ?? '',
      country: stored.country ?? '',
      // The stored embed is an address, and the field takes a paste. Showing
      // the address back is honest: it is what a re-save would keep.
      mapsEmbed: stored.mapsEmbedUrl ?? '',
    },
  };
}

/**
 * What this section contributes to a submission.
 *
 * `footer: null` when the box is unticked, which clears a footer a project used
 * to have rather than leaving an orphan behind. Blank fields are omitted; the
 * server treats a footer of nothing but blanks as no footer at all.
 */
export function localFieldsFrom(value: LocalSectionValue): {
  targetLocation: string;
  footer: FooterDetailsInput | null;
} {
  if (!value.footerEnabled) {
    return { targetLocation: value.targetLocation.trim(), footer: null };
  }

  const footer: FooterDetailsInput = {};
  for (const [field, entry] of Object.entries(value.footer) as [FooterField, string][]) {
    const trimmed = entry.trim();
    if (trimmed !== '') footer[field] = trimmed;
  }

  return { targetLocation: value.targetLocation.trim(), footer };
}

export interface LocalSectionFieldsProps {
  value: LocalSectionValue;
  onChange: (value: LocalSectionValue) => void;
  /** Prefills the footer's name field the first time the box is ticked. */
  targetName: string;
  errors?: LocalSectionErrors | undefined;
}

export function LocalSectionFields({
  value,
  onChange,
  targetName,
  errors,
}: LocalSectionFieldsProps): React.JSX.Element {
  const checkboxId = useId();

  function updateFooter(field: FooterField) {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ ...value, footer: { ...value.footer, [field]: event.target.value } });
    };
  }

  function toggleFooter(enabled: boolean): void {
    onChange({
      ...value,
      footerEnabled: enabled,
      // Prefilled from the target name, and editable from then on. Only ever
      // filled in when it is empty, so a name someone typed is never replaced.
      footer:
        enabled && value.footer.businessName.trim() === ''
          ? { ...value.footer, businessName: targetName.trim() }
          : value.footer,
    });
  }

  return (
    <div className="space-y-5">
      <TextField
        label="Target location"
        hint="One town or area."
        name="targetLocation"
        maxLength={MAX_TARGET_LOCATION}
        placeholder="Harbour Point"
        value={value.targetLocation}
        onChange={(event) => onChange({ ...value, targetLocation: event.target.value })}
        error={errors?.targetLocation}
      />

      <div className="flex items-start gap-3">
        <input
          id={checkboxId}
          type="checkbox"
          checked={value.footerEnabled}
          onChange={(event) => toggleFooter(event.target.checked)}
          className={`mt-1 h-4 w-4 shrink-0 rounded border-rule-strong text-fg focus:ring-blue-ink ${DISABLED_CHECKBOX}`}
        />
        <label htmlFor={checkboxId} className="text-sm text-fg">
          Sitewide footer with business details
        </label>
      </div>

      {value.footerEnabled ? (
        <div className="space-y-5 border-t border-rule-soft pt-5">
          <TextField
            label="Business name"
            name="footerBusinessName"
            maxLength={FIELD_LIMITS.businessName}
            value={value.footer.businessName}
            onChange={updateFooter('businessName')}
            error={errors?.footer?.businessName}
          />

          <TextField
            label="Phone"
            name="footerPhone"
            maxLength={FIELD_LIMITS.footerPhone}
            placeholder="01000 960 111"
            value={value.footer.phone}
            onChange={updateFooter('phone')}
            error={errors?.footer?.phone}
          />

          <TextField
            label="Address line 1"
            name="footerAddressLine1"
            maxLength={FIELD_LIMITS.footerAddressLine}
            placeholder="14 Quay Street"
            value={value.footer.addressLine1}
            onChange={updateFooter('addressLine1')}
            error={errors?.footer?.addressLine1}
          />

          <TextField
            label="Address line 2"
            hint="Optional."
            name="footerAddressLine2"
            maxLength={FIELD_LIMITS.footerAddressLine}
            value={value.footer.addressLine2}
            onChange={updateFooter('addressLine2')}
            error={errors?.footer?.addressLine2}
          />

          <TextField
            label="City or town"
            name="footerCity"
            maxLength={FIELD_LIMITS.footerCity}
            placeholder="Harbour Point"
            value={value.footer.city}
            onChange={updateFooter('city')}
            error={errors?.footer?.city}
          />

          <TextField
            label="Region or state"
            hint="Optional."
            name="footerRegion"
            maxLength={FIELD_LIMITS.footerRegion}
            value={value.footer.region}
            onChange={updateFooter('region')}
            error={errors?.footer?.region}
          />

          <TextField
            label="Postcode or ZIP"
            hint="Optional."
            name="footerPostcode"
            maxLength={FIELD_LIMITS.footerPostcode}
            value={value.footer.postcode}
            onChange={updateFooter('postcode')}
            error={errors?.footer?.postcode}
          />

          <CountrySelect
            label="Country"
            value={value.footer.country}
            onChange={(code) => onChange({ ...value, footer: { ...value.footer, country: code } })}
            error={errors?.footer?.country}
          />

          <TextAreaField
            label="Google Maps embed code"
            hint={
              'Optional. In Google Maps choose Share, then "Embed a map", and paste the whole ' +
              'snippet here. We keep the map address from it and nothing else.'
            }
            name="footerMapsEmbed"
            rows={3}
            value={value.footer.mapsEmbed}
            onChange={updateFooter('mapsEmbed')}
            error={errors?.footer?.mapsEmbed}
          />
        </div>
      ) : null}
    </div>
  );
}
